import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Linkedin } from "lucide-react";
import { api, SUBSTACK_URL } from "@/lib/api";
import { RevealLines, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";

export default function CorrespondentProfile() {
  const { id } = useParams();
  const [corr, setCorr] = useState(null);
  const [articles, setArticles] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/correspondents/${id}`).then(({ data }) => setCorr(data)).catch(() => setNotFound(true));
    api.get("/articles").then(({ data }) => setArticles(data)).catch(() => {});
  }, [id]);

  if (notFound)
    return (
      <div data-testid="correspondent-notfound" className="min-h-screen grid place-items-center px-6 text-center">
        <div>
          <p className="font-display text-4xl font-bold mb-4">Correspondent not found</p>
          <Link to="/about" className="label link-underline pb-1">Back to About</Link>
        </div>
      </div>
    );
  if (!corr) return <div className="min-h-screen grid place-items-center font-sans text-ink/50">Loading…</div>;

  const byThem = articles.filter(
    (a) => (a.author || "").trim().toLowerCase() === corr.name.trim().toLowerCase()
  );

  return (
    <div data-testid="correspondent-page">
      <section className="bg-ink text-white grain relative overflow-hidden pt-[110px] pb-20">
        <div className="relative z-10 max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12">
          <Link to="/about" data-testid="corr-back-link" className="inline-flex items-center gap-2 label text-white/50 hover:text-canary mb-10"><ArrowLeft size={15} /> Correspondents</Link>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <FadeUp className="lg:col-span-5">
              <div className="hover-img aspect-[4/5] bg-white/5">
                <img src={corr.photo} alt={corr.name} className="h-full w-full object-cover" />
              </div>
            </FadeUp>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="label text-canary mb-4">{corr.role}</p>
              <RevealLines as="h1" className="font-display text-5xl lg:text-6xl font-black tracking-tight leading-[0.9]" lines={corr.name.split(" ").reduce((acc, w, i) => { if (i % 2 === 0) acc.push(w); else acc[acc.length - 1] += " " + w; return acc; }, [])} />
              <div className="mt-7 space-y-4 text-lg text-white/75 leading-relaxed">
                {(corr.bio || "").split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <div className="flex flex-wrap gap-4 mt-8">
                {corr.substack && (
                  <a href={corr.substack} target="_blank" rel="noopener noreferrer" data-testid="corr-substack-btn"
                    className="inline-flex items-center gap-2 label bg-canary text-ink px-8 py-4 rounded-full hover:bg-white transition-colors duration-300">
                    Read on Substack <ArrowUpRight size={15} />
                  </a>
                )}
                {corr.linkedin && (
                  <a href={corr.linkedin} target="_blank" rel="noopener noreferrer" data-testid="corr-linkedin-btn"
                    className="inline-flex items-center gap-2 label border border-white/40 px-8 py-4 rounded-full hover:bg-white hover:text-ink transition-colors duration-300">
                    <Linkedin size={15} /> LinkedIn
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <FadeUp><h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight mb-10">Stories by {corr.name.split(" ")[0]}</h2></FadeUp>
        {byThem.length ? (
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {byThem.map((a) => (
              <StaggerItem key={a.id}>
                <Link to={`/article/${a.id}`} data-testid={`corr-article-${a.id}`} className="group block">
                  <div className="hover-img aspect-[4/3] bg-paper mb-5"><img src={a.cover_image} alt={a.title} className="h-full w-full object-cover" /></div>
                  <p className="label text-ink/50 mb-2">{a.category}</p>
                  <h3 className="font-display text-xl font-bold leading-snug group-hover:text-ink/60 transition-colors">{a.title}</h3>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        ) : (
          <p className="text-ink/50">Stories from {corr.name.split(" ")[0]} will appear here as they publish. Subscribe on{" "}
            <a href={corr.substack || SUBSTACK_URL} target="_blank" rel="noopener noreferrer" className="text-ink font-semibold link-underline pb-0.5">Substack</a> in the meantime.</p>
        )}
      </section>
    </div>
  );
}
